<script setup lang="ts">
import { ref } from "vue";

interface FaqItem {
  id: string;
  question: string;
  answer: string;
}

const openItems = ref<Set<string>>(new Set());

const toggleItem = (id: string) => {
  if (openItems.value.has(id)) {
    openItems.value.delete(id);
  } else {
    openItems.value.add(id);
  }
};

const formatAnswer = (answer: string) => {
  return answer
    .replace(
      /support@evangelist\.gg/g,
      '<a href="mailto:support@evangelist.gg" class="text-blue-600 hover:text-blue-800 underline">support@evangelist.gg</a>',
    )
    .replace(
      /(?<!@)(?<!mailto:)(?<!">)evangelist\.gg(?![^<]*<\/a>)/g,
      '<a href="https://evangelist.gg/" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">evangelist.gg</a>',
    );
};

const faqItems: FaqItem[] = [
  {
    id: "how-does-evangelist-work",
    question: "How does Evangelist work?",
    answer:
      "Evangelist helps projects grow by turning community members into product evangelists. When a campaign goes live, anyone can join by sharing posts on X. We track the performance of each post, evaluate its impact on real crypto audiences, and distribute rewards based on the quality of the content. Only eligible creators can see and participate in these campaigns.",
  },
  {
    id: "what-problem-does-evangelist-solve",
    question: "What problem does Evangelist solve?",
    answer:
      "Evangelist solves the problem of low-quality, artificial promotion in Web3. Many projects struggle with bot-driven hype, fake engagement, and unclear ROI from traditional marketing campaigns. Evangelist changes that by rewarding real users for reaching real crypto audiences. It gives projects a way to grow authentically and gives communities a fair, transparent way to earn for their impact.",
  },
  {
    id: "is-evangelist-free",
    question: "Is Evangelist free to use?",
    answer:
      "There's no cost to participate on Evangelist. Simply connect your X account to start joining campaigns, sharing content, and earning rewards effortlessly.",
  },
  {
    id: "qualify-for-campaign",
    question: "What do I need to do to qualify for a campaign?",
    answer:
      "To qualify for a campaign, you need to have a real, active account with genuine engagement from crypto users. Each campaign may also have specific criteria for participation.",
  },
  {
    id: "qualify-for-rewards",
    question: "What do I need to do to qualify for rewards?",
    answer:
      "To qualify for rewards, you need to participate in active campaigns by sharing or engaging with content as specified in each campaign's rules. Make sure your posts reach an authentic crypto audience and follow all guidelines. Keep your activity genuine and avoid any behaviors that might be considered manipulative, as these can disqualify you. The better your engagement with real users, the higher your chances of earning rewards.",
  },
  {
    id: "content-quality-measurement",
    question:
      "How do you measure content quality and what behaviors are considered manipulative?",
    answer:
      "We measure content quality based on real engagement from genuine crypto users. This includes original, relevant posts and natural interactions, not automated or fake activity. Using bots, spam, or tools to create fake engagement is considered manipulative and goes against our rules.",
  },
  {
    id: "detect-manipulative-behavior",
    question: "How do we detect manipulative behavior?",
    answer:
      "Detection is mostly handled by our internal systems, which look for suspicious patterns in engagement and posting.",
  },
  {
    id: "how-many-posts",
    question: "How many posts should I write for each campaign?",
    answer:
      "Each campaign outlines how many posts are eligible for rewards in its description. To keep the focus on meaningful contributions, only a limited number of your best-performing posts will be considered. Additional posts beyond that limit won't increase your rewards. We value quality content over volume.",
  },
  {
    id: "not-seeing-campaign",
    question: "Why am I not seeing a campaign?",
    answer:
      "Campaign visibility depends on eligibility criteria set by each campaign. During the sign-up and evaluation process, some accounts may be determined as ineligible for specific campaigns. If you're not seeing a campaign, it's likely because it doesn't currently apply to your profile. Also, if you meet the eligibility criteria after a campaign has already started, you won't become eligible for that campaign, however you may qualify for future campaigns. New opportunities are added regularly, so keep an eye out.",
  },
  {
    id: "not-eligible",
    question: "Why am I not eligible to join a campaign?",
    answer:
      "Most of the campaigns have eligibility criteria of over 500 followers and account age over 6 months which are checked at the beginning when the campaign starts or in a moment where the user joins the platform.",
  },
  {
    id: "participate-multiple-times",
    question: "Can I participate more than once?",
    answer:
      "You're welcome to join multiple campaigns. However, please note that each campaign may have its own eligibility criteria and participation limits. For instance, some campaigns may allow a maximum of two qualifying posts per participant. Always check the specific guidelines of each campaign to ensure compliance and maximize your rewards. Also keep in mind that posting too many promotions too quickly can lower your creator health score, which may affect your rewards. Focus on quality over quantity to keep your score high and maximize rewards.",
  },
  {
    id: "multiple-languages",
    question: "Do you support content in languages other than English?",
    answer:
      "Yes, we support content in multiple languages. Our advanced algorithms are designed to fairly evaluate content across different regional languages, ensuring every contribution is accurately recognized and rewarded.",
  },
  {
    id: "health-score",
    question: "How and why is my health score determined?",
    answer:
      "Your health score reflects the quality of your past content and how well your account performs in reaching an authentic crypto audience. It takes into account things like organic impressions, engagement from real users, and the overall potential of your account to attract genuine crypto enthusiasts. A healthy score helps us ensure that rewards go to accounts that contribute meaningfully, not just those chasing metrics.",
  },
  {
    id: "reward-delay",
    question: "Why haven't I received my reward yet?",
    answer:
      "Reward distribution doesn't happen instantly after a campaign ends. We take time to verify participation, ensure fair evaluation, and process payouts. Each campaign runs from Thursday to Thursday. After the final day, we begin reviewing all posts to ensure they meet the campaign criteria.",
  },
  {
    id: "evaluation-time",
    question: "Why does the evaluation take longer than 24 hours?",
    answer:
      "To ensure fairness and accuracy, each submission goes through a careful review process that may take longer than 24 hours. While this occasionally causes delays, it does not impact your eligibility or the calculation of rewards and everything will be counted once the evaluation is complete.",
  },
  {
    id: "supported-networks",
    question: "Which networks are supported?",
    answer:
      "Evangelist currently supports rewards on Solana, we will add EVM chains in the future. We're focused on building the best experience for the Solana ecosystem first, support for additional networks may come in the future.",
  },
  {
    id: "supported-wallets",
    question: "Which wallets are supported?",
    answer:
      "Evangelist currently supports any Solana-compatible wallet. You can connect your wallet easily during sign-up to receive rewards and participate in campaigns.",
  },
  {
    id: "ledger-verification",
    question: "How can I verify my Ledger wallet?",
    answer:
      "Ledger does not support off-chain message signing yet and therefore you need to make a transaction in order to verify their wallet. You need to make a small transaction (between 0.001-0.003 SOL, choose randomly) to the following address 3jmBUtdQnmy3gNXdUzLiak8ZFq8bfyrrSpYkqxpVMrfE.",
  },
  {
    id: "withdrawal-delay",
    question: "Why does my withdrawal take so long?",
    answer:
      "At the moment, rewards need manual oversight and are dispatched typically once in 24 hours. If you did not receive the reward within 24 hours, please first make sure you confirmed the 2fa verification. If you haven't, please cancel the withdrawal request and initiate it again or click on the withdrawal notification to reinitiate the 2fa verification.",
  },
  {
    id: "small-rewards",
    question: "Why did I not get any rewards/the reward is small?",
    answer:
      "Reward outcomes depend on several factors. In some cases, users may receive a smaller share due to high competition during the campaign period. In other cases, activity that violates campaign guidelines (such as using VPNs to bypass geo-restrictions or artificially boosting impressions) may lead to reduced or withheld rewards. To maintain fairness, we exclude suspicious or inauthentic engagement from final calculations. If you believe there's been a mistake, feel free to reach out via support@evangelist.gg we're happy to take a closer look.",
  },
  {
    id: "higher-rewards",
    question: "How to get higher rewards?",
    answer:
      "Rewards are based on the quality and reach of your engagement with the authentic crypto audience. While we don't disclose the exact algorithm (to ensure fairness and prevent gaming) the best way to increase your rewards is to participate consistently and meaningfully.\n\nHere are a few tips:\n\n• Engage genuinely: Posts that resonate with real crypto users tend to perform better. Focus on thoughtful, relevant contributions rather than volume.\n• Join more campaigns: You can increase your reward potential by participating in multiple campaigns. Just keep in mind that each campaign usually limits the number of qualifying posts (typically 2).\n• Be part of the community: Building your presence in the crypto space and connecting with others organically can help grow your reach and therefore your rewards.",
  },
  {
    id: "report-bug",
    question: "How do I report a bug or issue?",
    answer:
      "If something isn't working as expected, please email us at support@evangelist.gg or report the issue via our discord channel. Include a brief description of the issue, your username, and any relevant screenshots. This helps us investigate and fix the problem more efficiently.",
  },
  {
    id: "leaderboard-cheating",
    question: "Why are you letting people cheat the leaderboard?",
    answer:
      "The leaderboard is a visual representation of current score achieved by posting, however it does not showcase the actual distribution. The payout is calculated after the campaign evaluation finishes based on multiple factors including creator quality and behavior.",
  },
  {
    id: "mobile-version",
    question: "Is there a mobile version?",
    answer:
      "Yes, Evangelist offers a mobile-friendly experience. While there isn't a dedicated mobile app available for download, the platform is optimized for mobile browsers, allowing you to participate in campaigns, track your rewards, and manage your account directly from your smartphone. Simply visit evangelist.gg on your mobile device to access all features seamlessly.",
  },
  {
    id: "company-participation",
    question: "Is it possible for companies to take part?",
    answer:
      "Currently, participation is designed for individual users only. If your company is interested in collaborating or running campaigns, please get in touch with us to explore partnership opportunities.",
  },
];
</script>

<template>
  <div>
    <div class="container mx-auto px-6 pt-16">
      <div class="text-center mb-16">
        <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
          Frequently Asked Questions
        </h2>
        <p class="text-xl text-gray-600 max-w-3xl mx-auto">
          Get answers to common questions about Evangelist and how our platform
          works
        </p>
      </div>

      <div class="max-w-4xl mx-auto">
        <div class="space-y-4">
          <div
            v-for="item in faqItems"
            :key="item.id"
            class="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <button
              class="w-full px-6 py-6 text-left flex items-center justify-between focus:outline-none rounded-xl"
              @click="toggleItem(item.id)"
            >
              <h3 class="text-lg font-semibold text-gray-900 pr-8">
                {{ item.question }}
              </h3>
              <div class="flex-shrink-0">
                <svg
                  :class="[
                    'w-5 h-5 text-gray-500 transition-transform duration-200',
                    openItems.has(item.id) ? 'rotate-180' : '',
                  ]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </button>

            <div
              v-if="openItems.has(item.id)"
              class="px-6 pb-6 pt-6 border-t border-gray-100"
            >
              <div class="prose prose-gray max-w-none">
                <p
                  class="text-gray-700 leading-relaxed whitespace-pre-line"
                  v-html="formatAnswer(item.answer)"
                ></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.prose a {
  color: #2563eb;
  text-decoration: none;
}

.prose a:hover {
  color: #1e40af;
  text-decoration: underline;
}
</style>
